import { isNativeMobilePlatform } from '@core/mobile/isNativeMobilePlatform';
import {
  setVirtualKeyboardHeight,
  setVirtualKeyboardVisible,
} from '@core/mobile/virtualKeyboard';
import { isEditableInput } from '@core/util/isEditableInput';
import { isIOS } from '@solid-primitives/platform';
import { onCleanup, onMount } from 'solid-js';

function resetVirtualKeyboardState() {
  setVirtualKeyboardVisible(false);
  setVirtualKeyboardHeight(0);
  document.documentElement.style.setProperty('--dvh', '1dvh');
  document.documentElement.style.setProperty(
    '--virtual-keyboard-height',
    '0px'
  );
}

/**
 * Functionality for responding to virtual keyboard appearance in web app and native mobile app.
 */
export function useAppSquishHandlers() {
  if (isNativeMobilePlatform()) {
    type VirtualKeyboardEvent = CustomEventInit<{
      height: number;
      duration: number;
    }>;

    const handleKeyboardWillShow = (event: VirtualKeyboardEvent) => {
      setVirtualKeyboardVisible(true);
      setVirtualKeyboardHeight(event.detail?.height ?? 0);
      const newViewportHeight =
        (window.visualViewport?.height ?? 0) - (event.detail?.height ?? 0);
      const dvh = newViewportHeight * 0.01;
      document.documentElement.style.setProperty('--dvh', `${dvh}px`);
      document.documentElement.style.setProperty(
        '--virtual-keyboard-height',
        `${event.detail?.height ?? 0}px`
      );
    };

    const handleKeyboardWillHide = () => {
      resetVirtualKeyboardState();
    };

    const handleVisibilityChange = () => {
      if (
        document.visibilityState === 'visible' &&
        !isEditableInput(document.activeElement)
      ) {
        resetVirtualKeyboardState();
      }
    };

    onMount(() => {
      document.documentElement.style.setProperty(
        '--virtual-keyboard-height',
        '0px'
      );
      document.documentElement.style.setProperty('--dvh', '1dvh');
      window.addEventListener('keyboardWillShow', handleKeyboardWillShow);
      window.addEventListener('keyboardWillHide', handleKeyboardWillHide);
      document.addEventListener('visibilitychange', handleVisibilityChange);

      onCleanup(() => {
        window.removeEventListener('keyboardWillShow', handleKeyboardWillShow);
        window.removeEventListener('keyboardWillHide', handleKeyboardWillHide);
        document.removeEventListener(
          'visibilitychange',
          handleVisibilityChange
        );
      });
    });
  } else if (isIOS) {
    // We are tracking viewport height, and using that to set a CSS variable,
    // so that we can properly constrain the viewport-height for mobile in response to changes such as
    // the virtual keyboard appearing.
    let previousViewportHeight = window.visualViewport?.height || 0;
    const handleResize = () => {
      if (window.visualViewport) {
        const newViewportHeight = window.visualViewport.height;
        // We can reliably use the visual viewport shrinking in size as a strong signal that the virtual keyboard is visible. This is better than using focusIn as a signal, because with focusIn we have to guess the timing of the virtual keyboard appearing.
        // We can NOT reliably use the visual viewport growing for the inverse. Recent versions of iOS Safari cause this to fire immediately on virtual keyboard appearance. Instead we need to rely on focusOut.
        if (newViewportHeight < previousViewportHeight) {
          setVirtualKeyboardVisible(true);
          const vh = newViewportHeight * 0.01;
          document.documentElement.style.setProperty('--dvh', `${vh}px`);
          setTimeout(() => {
            window.scrollTo(0, 0);
          });
        }
        previousViewportHeight = newViewportHeight;
      }
    };

    const handleFocusOut = (e: FocusEvent) => {
      if (
        e.target instanceof Element &&
        isEditableInput(e.target) &&
        (!e.relatedTarget ||
          (e.relatedTarget instanceof Element &&
            !isEditableInput(e.relatedTarget)))
      ) {
        document.documentElement.style.setProperty('--dvh', '1dvh');
        setVirtualKeyboardVisible(false);
      }
    };

    onMount(() => {
      document.documentElement.style.setProperty(
        '--virtual-keyboard-height',
        '0px'
      );
      document.documentElement.style.setProperty('--dvh', '1dvh');
      if (window.visualViewport) {
        window.visualViewport.addEventListener('resize', handleResize);
        handleResize();
        window.visualViewport.addEventListener('scroll', handleResize);
      }
      document.addEventListener('focusout', handleFocusOut, { capture: true });

      onCleanup(() => {
        if (window.visualViewport) {
          window.visualViewport.removeEventListener('resize', handleResize);
          window.visualViewport.removeEventListener('scroll', handleResize);
        }
        document.removeEventListener('focusout', handleFocusOut, {
          capture: true,
        });
      });
    });
  }
}
